import apiClient from './api';
import { PaymentRecord, PaymentIntent, ApiResponse, PromptPayQRCode } from '../types/api';

// Interface for promotion code details
interface PromotionCodeDetails {
  isValid: boolean;
  percentOff?: number;
  amountOff?: number;
  couponId?: string;
  description?: string;
  allowedPaymentTypes?: ('one_time' | 'subscription')[];
  duration?: 'once' | 'forever' | 'repeating';
  durationInMonths?: number | null;
  minimumAmount?: number;
  currency?: string | null;
  errorMessage?: string;
  error?: string;
}

// Extended API response type that includes potential error fields
interface ApiResponseWithError<T> extends ApiResponse<T> {
  error?: string;
  message?: string;
  
  // Direct promotion code fields that might be present in the response
  isValid?: boolean;
  percentOff?: number;
  amountOff?: number;
  couponId?: string;
  description?: string;
  allowedPaymentTypes?: ('one_time' | 'subscription')[];
  duration?: 'once' | 'forever' | 'repeating';
  durationInMonths?: number | null;
  minimumAmount?: number;
  currency?: string | null;
}

export const paymentService = {
  /**
   * Get payment history for the current user
   */
  getPaymentHistory: async (): Promise<PaymentRecord[]> => {
    const response = await apiClient.get<{payments: PaymentRecord[]}>('/payments/history');
    return response.data.payments || [];
  },
  
  /**
   * Create a payment intent for a subscription
   */
  createPaymentIntent: async (
    subscriptionId: string, 
    amount: number,
    paymentType: 'one_time' | 'subscription' = 'one_time',
    paymentMethod?: string,
    promotionCode?: string,
    currency: 'thb' | 'usd' = 'thb'
  ): Promise<PaymentIntent> => {
    try {
      // If using payment methods that require email (like promptpay), the email
      // will be retrieved by the server from the user's profile
      const response = await apiClient.post<any>('/payments/intent', {
        subscription_id: subscriptionId,
        amount,
        payment_type: paymentType,
        payment_method: paymentMethod,
        promotion_code: promotionCode,
        currency
      });
      
      if (!response.data) {
        throw new Error('Empty response from payment API');
      }
      
      if (response.data.error) {
        throw new Error(`Payment API error: ${response.data.error}`);
      }
      
      if (response.data.clientSecret && response.data.subscriptionId) {
        return response.data as PaymentIntent;
      } else if (response.data.data && response.data.data.clientSecret) {
        return response.data.data as PaymentIntent;
      } else {
        throw new Error('Invalid payment intent data format');
      }
    } catch (error: any) {
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        console.error('Error response headers:', error.response.headers);
      } else if (error.request) {
        console.error('Error request:', error.request);
      } else {
        console.error('Error message:', error.message);
      }
      
      throw error;
    }
  },
  
  /**
   * Create a true Stripe subscription using Stripe Billing
   */
  createStripeSubscription: async (
    customerId: string | undefined,
    priceId: string, 
    paymentMethod: string = 'card',
    metadata?: Record<string, string>,
    promotionCode?: string,
    currency: 'thb' | 'usd' = 'thb'
  ): Promise<{ 
    subscriptionId: string, 
    clientSecret: string 
  }> => {
    try {
      const response = await apiClient.post('/payments/create-stripe-subscription', {
        customer_id: customerId,
        price_id: priceId,
        payment_method: paymentMethod,
        metadata,
        promotion_code: promotionCode,
        currency
      });
      
      if (!response.data) {
        throw new Error('Empty response from Stripe subscription API');
      }
      
      return { 
        subscriptionId: response.data.subscription_id,
        clientSecret: response.data.client_secret 
      };
    } catch (error: any) {
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
      }
      
      throw new Error('Failed to create Stripe subscription');
    }
  },
  
  /**
   * Get promotion code details
   */
  getPromotionCodeDetails: async (code: string): Promise<PromotionCodeDetails> => {
    try {
      // Add a short delay to avoid network race conditions
      await new Promise(resolve => setTimeout(resolve, 100));
      
      console.log(`Requesting promotion code details for: ${code}`);
      
      const response = await apiClient.get<ApiResponseWithError<PromotionCodeDetails>>(`/promotion-codes/${code}`);
      
      // Log the full response for debugging
      console.log(`API response for promotion code ${code}:`, response.data);
      
      if (!response.data) {
        return {
          isValid: false,
          error: 'Empty response from API'
        };
      }
      
      // Check for error message in response
      if (response.data.error) {
        return {
          isValid: false,
          error: response.data.error
        };
      }
      
      // First, check if the promotion data is directly in response.data
      if (response.data.isValid !== undefined) {
        // API returned promotion details directly in the response.data
        return {
          isValid: !!response.data.isValid,
          percentOff: response.data.percentOff || 0,
          amountOff: response.data.amountOff || 0,
          couponId: response.data.couponId || code,
          description: response.data.description || `Discount: ${response.data.percentOff ? `${response.data.percentOff}%` : response.data.amountOff ? `฿${response.data.amountOff}` : code}`,
          allowedPaymentTypes: response.data.allowedPaymentTypes || [],
          duration: response.data.duration || 'once',
          minimumAmount: response.data.minimumAmount || 0,
          currency: response.data.currency || null
        };
      }
      
      // Handle case where data object might contain error info
      if (response.data.data && typeof response.data.data === 'object') {
        if ('error' in response.data.data) {
          return {
            isValid: false,
            error: response.data.data.error as string || 'Invalid promotion code'
          };
        }
        
        // Extract valid data and ensure isValid is set
        const validData = response.data.data;
        
        return {
          // Ensure we have proper values with defaults
          isValid: true,
          percentOff: validData.percentOff || 0,
          amountOff: validData.amountOff || 0,
          couponId: validData.couponId || code,
          description: validData.description || `Discount: ${validData.percentOff ? `${validData.percentOff}%` : validData.amountOff ? `฿${validData.amountOff}` : code}`,
          allowedPaymentTypes: validData.allowedPaymentTypes || [],
          duration: validData.duration || 'once',
          minimumAmount: validData.minimumAmount || 0,
          currency: validData.currency || null
        };
      }
      
      // Default invalid response
      return {
        isValid: false,
        error: 'Invalid or expired promotion code'
      };
    } catch (error: any) {
      console.error('Error fetching promotion code details:', error);
      
      // Extract more detailed error information
      let errorMessage = 'Error processing promotion code';
      
      if (error.response && error.response.data) {
        console.log('API error response:', error.response.data);
        
        if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        isValid: false,
        error: errorMessage
      };
    }
  },
  
  /**
   * Get payment details by payment intent ID
   */
  getPaymentDetails: async (
    paymentIntentId: string
  ): Promise<{ subscriptionId: string }> => {
    try {
      const response = await apiClient.get<ApiResponse<any>>(`/payments/details/${paymentIntentId}`);
      
      if (!response.data || !response.data.data) {
        throw new Error('Failed to get payment details');
      }
      
      return {
        subscriptionId: response.data.data.subscription_id
      };
    } catch (error) {
      console.error('Error fetching payment details:', error);
      throw new Error('Failed to get payment details');
    }
  },
  
  /**
   * Get QR code for PromptPay payment
   */
  getPaymentQrCode: async (
    paymentIntentId: string,
    paymentMethod?: string,
    email?: string
  ): Promise<PromptPayQRCode> => {
    try {
      // Build query params including email if provided
      const queryParams = new URLSearchParams();
      if (paymentMethod) {
        queryParams.append('method', paymentMethod);
      }
      if (email) {
        queryParams.append('email', email);
      }
      
      const queryString = queryParams.toString();
      const endpoint = queryString 
        ? `/payments/details/${paymentIntentId}/qr?${queryString}`
        : `/payments/details/${paymentIntentId}/qr`;
        
      const response = await apiClient.get<ApiResponse<PromptPayQRCode>>(endpoint);
      
      if (!response.data || !response.data.data) {
        throw new Error('Failed to get PromptPay QR code');
      }
      
      return response.data.data;
    } catch (error) {
      console.error('Error fetching PromptPay QR code:', error);
      throw error;
    }
  },
  
  /**
   * Confirm a payment
   */
  confirmPayment: async (
    paymentIntentId: string
  ): Promise<{ success: boolean }> => {
    const response = await apiClient.post<ApiResponse<{ success: boolean }>>('/payments/confirm', {
      paymentIntentId
    });
    return response.data.data;
  },
  
  /**
   * Attempt to manually confirm a payment intent with a specific payment method
   */
  manualConfirmPayment: async (
    clientSecret: string,
    paymentMethod: string = 'promptpay'
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await apiClient.post<ApiResponse<any>>('/payments/manual-confirm', {
        client_secret: clientSecret,
        payment_method: paymentMethod
      });
      
      if (!response.data || !response.data.data) {
        throw new Error('Failed to confirm payment manually');
      }
      
      return response.data.data;
    } catch (error) {
      console.error('Error manually confirming payment:', error);
      throw error;
    }
  },
  
  /**
   * Create payment session for TokenSubs cryptocurrency payments
   * @param subscriptionId - The ID of the subscription
   * @param amount - The payment amount
   * @param currency - The currency code (THB or USD)
   * @param customerEmail - Optional customer email
   */
  createTokenSubsPayment: async (
    subscriptionId: string,
    amount: number,
    currency: string,
    customerEmail?: string
  ) => {
    try {
      const response = await apiClient.post('/payments/tokensubs/create', {
        subscription_id: subscriptionId,
        amount,
        currency: currency.toUpperCase(),
        customer_email: customerEmail
      });
      
      return response.data;
    } catch (error) {
      console.error('Error creating TokenSubs payment:', error);
      throw error;
    }
  }
};

export default paymentService; 